import React, { RefObject, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Account, TransactionCategoryDetail, TransactionDetailValue, Transaction, Currency, TransactionCustomField, CustomFieldAttachmentValue } from "../../../../types";
import { TransactionFieldIcon } from "../../../TransactionFieldsSettings/components/TransactionFieldIcon";
import RefreshIconButton from "../../../../components/RefreshIconButton";
import { validateAmountDecimalPlaces, formatAmountToCurrencyDecimalPlaces, convertFormattedAmountToRawValue, getCurrencyDecimalPlaces } from "../../../../utils/currencyDefaults";
import { formatCurrency, formatRate, convertTo12Hour } from "../../../../utils/transactionUtils";
import { type FocusedField } from "../hooks/useMobileFieldFocus";

type TransactionType = "expense" | "income" | "transfer";

interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: Record<string, string | CustomFieldAttachmentValue>;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: Record<string, string | CustomFieldAttachmentValue>;
  customFieldPendingFiles?: Record<string, File>;
}

interface MobileTransactionFormFieldsProps {
  transactionType: TransactionType;
  isEditMode: boolean;
  editingTransaction: Transaction | null;
  focusedField: FocusedField;
  setFocusedField: (field: FocusedField) => void;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  selectedCategoryDetails: TransactionCategoryDetail[];
  newlyAddedDetailOptions: Record<string, string[]>;
  setNewlyAddedDetailOptions: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  openDetailLists: Record<string, boolean>;
  setOpenDetailLists: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  addOptionDialogOpen: boolean;
  setAddOptionDialogDetailName: (name: string) => void;
  setAddOptionDialogValue: (value: string) => void;
  setAddOptionDialogError: (error: string) => void;
  setAddOptionDialogOpen: (open: boolean) => void;
  uploadingAttachment: boolean;
  isOnOptionalField: boolean;
  setIsOnOptionalField: (value: boolean) => void;
  keepTimePickerOpen: boolean;
  setKeepTimePickerOpen: (value: boolean) => void;
  rateManuallyEdited: boolean;
  defaultCurrencyRateManuallyEdited: boolean;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  handleRefreshRate: () => void;
  handleRefreshDefaultCurrencyRate: () => void;
  handleFieldFocus: (field: FocusedField) => void;
  handleFieldBlur: (field: FocusedField) => void;
  handleSubmit: (e: React.FormEvent) => void;
  formatDateForDisplay: (dateString: string) => string;
  getCurrentTime: () => string;
  timeFormat?: "12h" | "24h"; // Time format for display
  getCalculatorDisplayValue: () => string;
  parseExpressionIntoCalculator: (expression: string) => void;
  setCalculatorExpression: (expression: string) => void;
  setCalculatorResetKey: (key: number | ((prev: number) => number)) => void;
  amountInputRef: RefObject<HTMLInputElement | null>;
  customFieldFileInputRef: RefObject<HTMLInputElement | null>;
  transferCustomFieldFileInputRef: RefObject<HTMLInputElement | null>;
  expenseCustomFields: TransactionCustomField[];
  incomeCustomFields: TransactionCustomField[];
  transferCustomFields: TransactionCustomField[];
  handleAccountSelect: (accountId: string) => void;
  handleCurrencySelect: (currencyCode: string) => void;
  hookHandleCategorySelect: (categoryName: string) => void;
  checkDuplicateOption: (value: string, detailName: string) => string;
  handleAddDetailOption: () => void;
  handleCloseAddOptionDialog: () => void;
  addOptionDialogValue: string;
  addOptionDialogDetailName: string;
  addOptionDialogError: string;
  isIOS: boolean;
  showTransferFee?: boolean;
  setShowTransferFee?: (show: boolean) => void;
  allowExpenseFee?: boolean;
  allowTransferFee?: boolean;
}

const MobileTransactionFormFields: React.FC<MobileTransactionFormFieldsProps> = ({
  transactionType,
  isEditMode,
  editingTransaction,
  focusedField,
  setFocusedField,
  formData,
  setFormData,
  transferFormData,
  setTransferFormData,
  accounts,
  currencies,
  defaultCurrency,
  selectedCategoryDetails,
  newlyAddedDetailOptions,
  setNewlyAddedDetailOptions,
  openDetailLists,
  setOpenDetailLists,
  addOptionDialogOpen,
  setAddOptionDialogDetailName,
  setAddOptionDialogValue,
  setAddOptionDialogError,
  setAddOptionDialogOpen,
  uploadingAttachment,
  isOnOptionalField,
  setIsOnOptionalField,
  keepTimePickerOpen,
  setKeepTimePickerOpen,
  rateManuallyEdited,
  defaultCurrencyRateManuallyEdited,
  calculateTransferRate,
  handleRefreshRate,
  handleRefreshDefaultCurrencyRate,
  handleFieldFocus,
  handleFieldBlur,
  handleSubmit,
  formatDateForDisplay,
  getCurrentTime,
  timeFormat = "24h",
  getCalculatorDisplayValue,
  parseExpressionIntoCalculator,
  setCalculatorExpression,
  setCalculatorResetKey,
  amountInputRef,
  customFieldFileInputRef,
  transferCustomFieldFileInputRef,
  expenseCustomFields,
  incomeCustomFields,
  transferCustomFields,
  handleAccountSelect,
  handleCurrencySelect,
  hookHandleCategorySelect,
  checkDuplicateOption,
  handleAddDetailOption,
  handleCloseAddOptionDialog,
  addOptionDialogValue,
  addOptionDialogDetailName,
  addOptionDialogError,
  isIOS,
  showTransferFee,
  setShowTransferFee,
  allowExpenseFee = false,
  allowTransferFee = false,
}) => {
  const customFieldFileTargetRef = useRef<string | null>(null);
  const customFields = transactionType === "transfer" ? transferCustomFields : (transactionType === "income" ? incomeCustomFields : expenseCustomFields);
  const isTransfer = transactionType === "transfer";

  return (
    <Stack spacing={focusedField === "amount" ? 1.5 : 2} sx={{ width: "100%", maxWidth: "100%" }}>
          {/* Date and Time - Time shown inline on right side of date field */}
          {(() => {
            const currentTime = transactionType === "transfer" ? transferFormData.time : formData.time;
            const hasTime = currentTime && currentTime.trim() !== "";
            const isTimeFocused = focusedField === "time";
            const isDateFocused = focusedField === "date";
            const hasTimeValue = currentTime && currentTime.trim() !== "";
            
            // Format time for display according to workspace setting
            const formatTimeForDisplay = (time24: string): string => {
              if (!time24 || !time24.trim()) return "";
              if (timeFormat === "12h") {
                const { hour, minute, period } = convertTo12Hour(time24);
                return `${hour}:${minute} ${period}`;
              }
              return time24; // Already in 24-hour format
            };
            
            const displayTime = hasTimeValue ? formatTimeForDisplay(currentTime) : "";
            
            const shouldShowTimeButton = !hasTime && !isTimeFocused && (!isEditMode || (isEditMode && !hasTime));
            const shouldShowTimeInline = hasTimeValue || focusedField === "time";
            
            return (
              <Box data-field="date" sx={{ width: "100%" }}>
              <TextField
                type="text"
                fullWidth
                label="Date"
                value={formatDateForDisplay(transactionType === "transfer" ? transferFormData.date : formData.date)}
                placeholder="mm/dd/yyyy"
                inputMode="none"
                autoComplete="off"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (focusedField === "time") {
                    setKeepTimePickerOpen(true);
                  }
                  if (focusedField === "amount" || focusedField === "rate") {
                    setFocusedField(null);
                  }
                  handleFieldFocus("date");
                }}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLInputElement).blur();
                  if (focusedField === "time") {
                    setKeepTimePickerOpen(true);
                  }
                  if (focusedField === "amount" || focusedField === "rate") {
                    setFocusedField(null);
                  }
                  handleFieldFocus("date");
                }}
                onKeyDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                required
                tabIndex={-1}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon 
                        sx={{ 
                          color: focusedField === "date" ? "primary.main" : "text.secondary",
                          fontSize: "1.25rem",
                        }} 
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment 
                      position="end"
                      sx={{
                        margin: 0,
                        padding: 0,
                        minWidth: "auto",
                        position: "relative",
                      }}
                    >
                      {shouldShowTimeButton ? (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (focusedField === "date") {
                              setFocusedField(null);
                            }
                            if (isEditMode && !editingTransaction) {
                              return;
                            }
                            const newTime = getCurrentTime();
                            if (transactionType === "transfer") {
                              setTransferFormData((prev) => ({ ...prev, time: newTime }));
                            } else {
                              setFormData((prev) => ({ ...prev, time: newTime }));
                            }
                            handleFieldFocus("time");
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (focusedField === "date") {
                              setFocusedField(null);
                            }
                            if (isEditMode && !editingTransaction) {
                              return;
                            }
                            const newTime = getCurrentTime();
                            if (transactionType === "transfer") {
                              setTransferFormData((prev) => ({ ...prev, time: newTime }));
                            } else {
                              setFormData((prev) => ({ ...prev, time: newTime }));
                            }
                            handleFieldFocus("time");
                          }}
                          sx={{
                            minWidth: "auto",
                            px: 1,
                            py: 0.5,
                            color: "text.primary",
                            fontWeight: "normal",
                            textTransform: "none",
                            touchAction: "manipulation",
                            WebkitTapHighlightColor: "transparent",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          Time
                        </Button>
                      ) : shouldShowTimeInline ? (
                        <Box sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          gap: 0.5,
                          minWidth: 110,
                          width: 110,
                        }}>
                          <TextField
                            type="text"
                            value={displayTime}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (focusedField === "date") {
                                setFocusedField(null);
                              }
                              handleFieldFocus("time");
                            }}
                            onFocus={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              (e.target as HTMLInputElement).blur();
                              if (focusedField === "date") {
                                setFocusedField(null);
                              }
                              handleFieldFocus("time");
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              if (!isIOS) {
                                e.preventDefault();
                              }
                              if (focusedField === "date") {
                                setFocusedField(null);
                              }
                              handleFieldFocus("time");
                            }}
                            size="small"
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{ 
                              width: 100,
                              minWidth: 100,
                              flexShrink: 0,
                              "& .MuiOutlinedInput-root": {
                                height: 32,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                "& fieldset": {
                                  borderColor: (focusedField === "time") ? "primary.main" : "divider",
                                  borderWidth: (focusedField === "time") ? "2px" : "1px",
                                  transition: "border-color 0.2s ease",
                                },
                                "&:hover fieldset": {
                                  borderColor: (focusedField === "time") ? "primary.main" : "text.primary",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: (focusedField === "time") ? "primary.main" : "divider",
                                  borderWidth: (focusedField === "time") ? "2px" : "1px",
                                },
                              },
                              "& input": {
                                padding: "4px 8px",
                                fontSize: "0.875rem",
                                cursor: "pointer",
                                textAlign: "center",
                                lineHeight: "24px",
                                width: "100%",
                                color: "text.primary",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (transactionType === "transfer") {
                                setTransferFormData((prev) => ({ ...prev, time: "" }));
                              } else {
                                setFormData((prev) => ({ ...prev, time: "" }));
                              }
                              if (focusedField === "time" || keepTimePickerOpen) {
                                setFocusedField(null);
                                setKeepTimePickerOpen(false);
                              }
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (transactionType === "transfer") {
                                setTransferFormData((prev) => ({ ...prev, time: "" }));
                              } else {
                                setFormData((prev) => ({ ...prev, time: "" }));
                              }
                              if (focusedField === "time" || keepTimePickerOpen) {
                                setFocusedField(null);
                                setKeepTimePickerOpen(false);
                              }
                            }}
                            sx={{
                              color: "text.secondary",
                              touchAction: "manipulation",
                              WebkitTapHighlightColor: "transparent",
                              "&:hover": {
                                color: "text.primary",
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputLabel-root": {
                    color: focusedField === "date" ? "primary.main" : "text.secondary",
                    transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                    "&.Mui-focused": {
                      color: focusedField === "date" ? "primary.main" : "text.secondary",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    cursor: "pointer",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                    "& fieldset": {
                      borderColor: focusedField === "date" ? "primary.main" : "divider",
                      transition: "border-color 0.2s ease",
                    },
                    "&:hover fieldset": {
                      borderColor: focusedField === "date" ? "primary.main" : "text.primary",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: focusedField === "date" ? "primary.main" : "divider",
                    },
                  },
                  "& input": {
                    width: "100%",
                    minWidth: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    caretColor: "transparent",
                    WebkitUserSelect: "none",
                    userSelect: "none",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                    "&::placeholder": {
                      color: "text.secondary",
                      opacity: 0.6,
                    },
                  },
                }}
              />
              </Box>
            );
          })()}

          {/* Amount - Right after Date */}
          {transactionType === "transfer" ? (
            <>
              {(() => {
                // Get currency from fromAccount
                const fromAccount = transferFormData.fromAccountId 
                  ? accounts.find((acc) => acc.id === transferFormData.fromAccountId)
                  : null;
                const transferCurrency = fromAccount?.currency || defaultCurrency;
                
                // Get current amount value (from calculator or transferFormData)
                const currentAmountValue = focusedField === "amount" ? getCalculatorDisplayValue() : transferFormData.amount;
                
                // Validate decimal places
                const validation = validateAmountDecimalPlaces(
                  currentAmountValue,
                  transferCurrency,
                  currencies,
                  defaultCurrency
                );
                
                // Calculate converted amount for helperText when currencies differ
                const calculationHelperText = (() => {
                  if (!transferFormData.fromAccountId || !transferFormData.toAccountId || !transferFormData.amount) return null;
                  
                  const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
                  const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);
                  if (!fromAccount || !toAccount) return null;
                  
                  const currenciesDiffer = fromAccount.currency !== toAccount.currency;
                  if (!currenciesDiffer) return null;
                  
                  const transferAmount = parseFloat(transferFormData.amount);
                  if (isNaN(transferAmount) || transferAmount <= 0) return null;
                  
                  // Get the rate to use: stored rate if valid, otherwise calculate it
                  const storedRate = transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : null;
                  const calculatedRate = calculateTransferRate(fromAccount.currency, toAccount.currency);
                  
                  // Use stored rate if it's valid and reasonable, otherwise use calculated rate
                  // Validate rate: should be between 0.0001 and 10000 (covers most currency conversions)
                  const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
                  const rateToUse = isValidStoredRate ? storedRate : calculatedRate;
                  
                  // Final validation: ensure calculated rate is also reasonable
                  if (!rateToUse || isNaN(rateToUse) || rateToUse <= 0 || rateToUse < 0.0001 || rateToUse > 10000) {
                    return null;
                  }
                  
                  const convertedAmount = transferAmount * rateToUse;
                  
                  if (isNaN(convertedAmount) || convertedAmount <= 0) return null;
                  
                  const fromAccountDecimalPlaces = getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency);
                  const toAccountDecimalPlaces = getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency);
                  const formattedAmount = formatCurrency(transferAmount, fromAccount.currency, fromAccountDecimalPlaces);
                  const formattedConverted = formatCurrency(convertedAmount, toAccount.currency, toAccountDecimalPlaces);
                  
                  return `Calculation: ${formattedAmount} = ${formattedConverted}`;
                })();
                
                // Combine validation error and calculation helper text
                const helperText = validation.errorMessage 
                  ? (calculationHelperText ? `${validation.errorMessage} | ${calculationHelperText}` : validation.errorMessage)
                  : calculationHelperText || undefined;
                
                return (
                  <Box data-field="amount" sx={{ width: "100%" }}>
                  <TextField
                    inputRef={amountInputRef}
                    fullWidth
                    required
                    label="Amount"
                    value={currentAmountValue}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      if (focusedField === "time") {
                        setFocusedField(null);
                        setKeepTimePickerOpen(false);
                      }
                      // Convert formatted amount to raw value and update transferFormData immediately (like web version)
                      const value = transferFormData.amount || "";
                      const rawValue = value && value.trim() !== "" 
                        ? convertFormattedAmountToRawValue(value) 
                        : "0";
                      
                      if (value && value.trim() !== "" && rawValue !== value) {
                        // Update transferFormData immediately so display shows raw value
                        setTransferFormData({ ...transferFormData, amount: rawValue });
                      }
                      
                      // If calculator is already open, only update if user hasn't typed anything new
                      if (focusedField === "amount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        // Check if user is typing an expression (contains operators)
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        // Check if current value is longer than raw value (user is typing more digits, e.g., "5554" vs "555")
                        const isLonger = currentCalcValue.length > rawValue.length;
                        const startsWithRaw = currentCalcValue.startsWith(rawValue) || rawValue.startsWith(currentCalcValue);
                        
                        // Check if value is already correct - if so, don't update (prevents unnecessary resets)
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        
                        // Only update if calculator is empty/0 OR if it's different and doesn't have operators/isn't longer
                        // If calculator already matches rawValue/value, don't update (it's already correct)
                        // If calculator has operators or is longer, user is actively typing, so don't overwrite
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (
                                           currentCalcValue === "0" || 
                                           currentCalcValue === "");
                        
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        // Calculator is closed, set hook state and open normally
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("amount");
                      }
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.target as HTMLInputElement).blur();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      if (focusedField === "time") {
                        setFocusedField(null);
                        setKeepTimePickerOpen(false);
                      }
                      // Convert formatted amount to raw value and update transferFormData immediately (like web version)
                      const value = transferFormData.amount || "";
                      const rawValue = value && value.trim() !== "" 
                        ? convertFormattedAmountToRawValue(value) 
                        : "0";
                      
                      if (value && value.trim() !== "" && rawValue !== value) {
                        // Update transferFormData immediately so display shows raw value
                        setTransferFormData({ ...transferFormData, amount: rawValue });
                      }
                      
                      // If calculator is already open, only update if user hasn't typed anything new
                      if (focusedField === "amount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        // Check if user is typing an expression (contains operators)
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        // Check if current value is longer than raw value (user is typing more digits, e.g., "5554" vs "555")
                        const isLonger = currentCalcValue.length > rawValue.length;
                        const startsWithRaw = currentCalcValue.startsWith(rawValue) || rawValue.startsWith(currentCalcValue);
                        
                        // Check if value is already correct - if so, don't update (prevents unnecessary resets)
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        
                        // Only update if calculator is empty/0 OR if it's different and doesn't have operators/isn't longer
                        // If calculator already matches rawValue/value, don't update (it's already correct)
                        // If calculator has operators or is longer, user is actively typing, so don't overwrite
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (
                                           currentCalcValue === "0" || 
                                           currentCalcValue === "");
                        
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        // Calculator is closed, set hook state and open normally
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("amount");
                      }
                    }}
                    placeholder="0.00"
                    tabIndex={-1}
                    error={!validation.isValid}
                    helperText={helperText}
                    inputProps={{ inputMode: "none" }}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: focusedField === "amount" ? "primary.main" : "text.secondary",
                        transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                        "&.Mui-focused": {
                          color: focusedField === "amount" ? "primary.main" : "text.secondary",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: focusedField === "amount" ? "primary.main" : "divider",
                          transition: "border-color 0.2s ease",
                        },
                        "&:hover fieldset": {
                          borderColor: focusedField === "amount" ? "primary.main" : "text.primary",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: focusedField === "amount" ? "primary.main" : "divider",
                        },
                      },
                    }}
                  />
                  </Box>
                );
              })()}

              {/* Transfer fee - same pattern as expense fee (only when transfer fee is enabled) */}
              {transactionType === "transfer" && allowTransferFee && setShowTransferFee && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    type="button"
                    size="small"
                    onClick={() => {
                      if (transferFormData.feeAmount === undefined) {
                        setShowTransferFee(true);
                        setTransferFormData({ ...transferFormData, feeAmount: "", feeAccountId: "" });
                        setCalculatorExpression("0");
                        setTimeout(() => handleFieldFocus("feeAmount"), 0);
                      } else {
                        setTransferFormData({ ...transferFormData, feeAmount: undefined, feeAccountId: undefined });
                        setShowTransferFee(false);
                      }
                    }}
                    disabled={!!(editingTransaction && editingTransaction.type === "transfer" && transferFormData.feeAmount !== undefined && transferFormData.feeAmount !== "")}
                    sx={{ minWidth: "auto", px: 1, py: 0.5, textTransform: "none" }}
                  >
                    {transferFormData.feeAmount === undefined ? "+ Add fee" : "Remove fee"}
                  </Button>
                </Box>
              )}

              {/* Fee Fields - Show when feeAmount is set (transfer only, and only when transfer fee is enabled) */}
              {transactionType === "transfer" && allowTransferFee && transferFormData.feeAmount !== undefined && (
                <Stack direction="row" spacing={2}>
                  <Box data-field="feeAmount" sx={{ flex: 1 }}>
                  <TextField
                    required
                    label="Fee Amount"
                    value={focusedField === "feeAmount" ? getCalculatorDisplayValue() : (transferFormData.feeAmount || "")}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (focusedField === "date") setFocusedField(null);
                      const value = transferFormData.feeAmount || "";
                      const rawValue = value && value.trim() !== "" ? convertFormattedAmountToRawValue(value) : "0";
                      if (value && value.trim() !== "" && rawValue !== value) {
                        setTransferFormData({ ...transferFormData, feeAmount: rawValue });
                      }
                      if (focusedField === "feeAmount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        const isLonger = currentCalcValue.length > rawValue.length;
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (currentCalcValue === "0" || currentCalcValue === "");
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("feeAmount");
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      // Convert formatted amount to raw value and update transferFormData immediately (like web version)
                      const value = transferFormData.feeAmount || "";
                      const rawValue = value && value.trim() !== "" 
                        ? convertFormattedAmountToRawValue(value) 
                        : "0";
                      
                      if (value && value.trim() !== "" && rawValue !== value) {
                        // Update transferFormData immediately so display shows raw value
                        setTransferFormData({ ...transferFormData, feeAmount: rawValue });
                      }
                      
                      // If calculator is already open, only update if user hasn't typed anything new
                      if (focusedField === "feeAmount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        // Check if user is typing an expression (contains operators)
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        // Check if current value is longer than raw value (user is typing more digits)
                        const isLonger = currentCalcValue.length > rawValue.length;
                        
                        // Check if value is already correct - if so, don't update (prevents unnecessary resets)
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        
                        // Only update if calculator is empty/0 OR if it's different and doesn't have operators/isn't longer
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (
                                       currentCalcValue === "0" || 
                                       currentCalcValue === "");
                        
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        // Calculator is closed, set hook state and open normally
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("feeAmount");
                      }
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.target as HTMLInputElement).blur();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      // Convert formatted amount to raw value and update transferFormData immediately (like web version)
                      const value = transferFormData.feeAmount || "";
                      const rawValue = value && value.trim() !== "" 
                        ? convertFormattedAmountToRawValue(value) 
                        : "0";
                      
                      if (value && value.trim() !== "" && rawValue !== value) {
                        // Update transferFormData immediately so display shows raw value
                        setTransferFormData({ ...transferFormData, feeAmount: rawValue });
                      }
                      
                      // If calculator is already open, only update if user hasn't typed anything new
                      if (focusedField === "feeAmount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        // Check if user is typing an expression (contains operators)
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        // Check if current value is longer than raw value (user is typing more digits)
                        const isLonger = currentCalcValue.length > rawValue.length;
                        
                        // Check if value is already correct - if so, don't update (prevents unnecessary resets)
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        
                        // Only update if calculator is empty/0 OR if it's different and doesn't have operators/isn't longer
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (
                                       currentCalcValue === "0" || 
                                       currentCalcValue === "");
                        
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        // Calculator is closed, set hook state and open normally
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("feeAmount");
                      }
                    }}
                    onBlur={() => handleFieldBlur("feeAmount")}
                    inputProps={{ inputMode: "none", tabIndex: -1 }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: transferFormData.feeAccountId ? (
                        <InputAdornment position="end">
                          <Typography variant="body2" color="text.secondary">
                            {accounts.find((acc) => acc.id === transferFormData.feeAccountId && !acc.isArchived)?.currency || ""}
                          </Typography>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiInputLabel-root": {
                        color: focusedField === "feeAmount" ? "primary.main" : "text.secondary",
                        transition: "color 0.2s ease",
                        "&.Mui-focused": {
                          color: focusedField === "feeAmount" ? "primary.main" : "text.secondary",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: focusedField === "feeAmount" ? "primary.main" : "divider",
                          borderWidth: focusedField === "feeAmount" ? "2px" : "1px",
                          transition: "border-color 0.2s ease, border-width 0.2s ease",
                        },
                        "&:hover fieldset": {
                          borderColor: focusedField === "feeAmount" ? "primary.main" : "text.primary",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: focusedField === "feeAmount" ? "primary.main" : "divider",
                          borderWidth: focusedField === "feeAmount" ? "2px" : "1px",
                        },
                      },
                    }}
                  />
                  </Box>
                  <TextField
                    label="Fee Account"
                    value={accounts.find(acc => acc.id === transferFormData.feeAccountId)?.accountName || ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleFieldFocus("feeAccount");
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.target as HTMLInputElement).blur();
                      handleFieldFocus("feeAccount");
                    }}
                    onBlur={() => handleFieldBlur("feeAccount")}
                    required
                    InputProps={{
                      readOnly: true,
                      endAdornment: transferFormData.feeAccountId ? (
                        <InputAdornment position="end">
                          <Button
                            disabled
                            sx={{
                              minWidth: "auto",
                              px: 1,
                              py: 0.5,
                              color: "text.secondary",
                              fontWeight: "normal",
                              textTransform: "none",
                              cursor: "default",
                              "&.Mui-disabled": {
                                color: "text.secondary",
                              },
                            }}
                          >
                            {accounts.find(acc => acc.id === transferFormData.feeAccountId)?.currency || ""}
                          </Button>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    placeholder="Select account"
                    tabIndex={-1}
                    sx={{
                      flex: 1,
                      "& .MuiInputLabel-root": {
                        color: focusedField === "feeAccount" ? "primary.main" : "text.secondary",
                        transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                        "&.Mui-focused": {
                          color: focusedField === "feeAccount" ? "primary.main" : "text.secondary",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: focusedField === "feeAccount" ? "primary.main" : "divider",
                          borderWidth: focusedField === "feeAccount" ? "2px" : "1px",
                          transition: "border-color 0.2s ease, border-width 0.2s ease",
                        },
                        "&:hover fieldset": {
                          borderColor: focusedField === "feeAccount" ? "primary.main" : "text.primary",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: focusedField === "feeAccount" ? "primary.main" : "divider",
                          borderWidth: focusedField === "feeAccount" ? "2px" : "1px",
                        },
                      },
                    }}
                  />
                </Stack>
              )}
            </>
          ) : (
            <>
              {(() => {
                // Get current currency
                const currentCurrency = formData.currency || defaultCurrency;
                
                // Get current amount value (from calculator or formData)
                // When calculator is open, use calculator value (can be "0")
                // When calculator is closed, use formData value, but show "0" if empty
                const calcValue = focusedField === "amount" ? getCalculatorDisplayValue() : null;
                const formValue = formData.amount || "";
                // If calculator is open, use calculator value (show "0" if calculator shows "0" or empty)
                // If calculator is closed, use formData value, but show "0" if it's empty or "0"
                const currentAmountValue = calcValue !== null 
                  ? (calcValue === "" || calcValue === "0" ? "0" : calcValue)
                  : (formValue === "" || formValue === "0" ? "0" : formValue);
                
                // Validate decimal places
                const validation = validateAmountDecimalPlaces(
                  currentAmountValue,
                  currentCurrency,
                  currencies,
                  defaultCurrency
                );
                
                // Calculate converted amount for helperText when currencies differ
                const calculationHelperText = (() => {
                  if (!formData.accountId || !formData.currency || !formData.amount) return null;
                  
                  const account = accounts.find(acc => acc.id === formData.accountId);
                  const accountCurrency = account?.currency || defaultCurrency;
                  const transactionCurrency = formData.currency || defaultCurrency;
                  const currenciesDiffer = transactionCurrency !== accountCurrency;
                  
                  if (!currenciesDiffer) return null;
                  
                  const transactionAmount = parseFloat(formData.amount);
                  if (isNaN(transactionAmount) || transactionAmount <= 0) return null;
                  
                  // Get the rate to use: stored rate if valid, otherwise calculate it
                  const storedRate = formData.rate ? parseFloat(formData.rate) : null;
                  const calculatedRate = calculateTransferRate(transactionCurrency, accountCurrency);
                  
                  // Use stored rate if it's valid and reasonable, otherwise use calculated rate
                  // Validate rate: should be between 0.0001 and 10000 (covers most currency conversions)
                  const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
                  const rateToUse = isValidStoredRate ? storedRate : calculatedRate;
                  
                  // Final validation: ensure calculated rate is also reasonable
                  if (!rateToUse || isNaN(rateToUse) || rateToUse <= 0 || rateToUse < 0.0001 || rateToUse > 10000) {
                    return null;
                  }
                  
                  const convertedAmount = transactionAmount * rateToUse;
                  
                  if (isNaN(convertedAmount) || convertedAmount <= 0) return null;
                  
                  const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                  const transactionDecimalPlaces = getCurrencyDecimalPlaces(transactionCurrency, currencies, defaultCurrency);
                  const formattedAmount = formatCurrency(transactionAmount, transactionCurrency, transactionDecimalPlaces);
                  const formattedConverted = formatCurrency(convertedAmount, accountCurrency, accountDecimalPlaces);
                  
                  return `Calculation: ${formattedAmount} = ${formattedConverted}`;
                })();
                
                // Combine validation error and calculation helper text
                const helperText = validation.errorMessage 
                  ? (calculationHelperText ? `${validation.errorMessage} | ${calculationHelperText}` : validation.errorMessage)
                  : calculationHelperText || undefined;
                
                return (
                  <Box data-field="amount" sx={{ width: "100%" }}>
                  <TextField
                    inputRef={amountInputRef}
                    fullWidth
                    required
                    label="Amount"
                    value={currentAmountValue}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      if (focusedField === "time") {
                        setFocusedField(null);
                        setKeepTimePickerOpen(false);
                      }
                      // Convert formatted amount to raw value and update formData immediately (like web version)
                      const value = formData.amount || "";
                      const rawValue = value && value.trim() !== "" 
                        ? convertFormattedAmountToRawValue(value) 
                        : "0";
                      
                      if (value && value.trim() !== "" && rawValue !== value) {
                        // Update formData immediately so display shows raw value
                        setFormData({ ...formData, amount: rawValue });
                      }
                      
                      // If calculator is already open, only update if user hasn't typed anything new
                      if (focusedField === "amount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        // Check if user is typing an expression (contains operators)
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        // Check if current value is longer than raw value (user is typing more digits, e.g., "5554" vs "555")
                        const isLonger = currentCalcValue.length > rawValue.length;
                        const startsWithRaw = currentCalcValue.startsWith(rawValue) || rawValue.startsWith(currentCalcValue);
                        
                        // Check if value is already correct - if so, don't update (prevents unnecessary resets)
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        
                        // Only update if calculator is empty/0 OR if it's different and doesn't have operators/isn't longer
                        // If calculator already matches rawValue/value, don't update (it's already correct)
                        // If calculator has operators or is longer, user is actively typing, so don't overwrite
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (
                                           currentCalcValue === "0" || 
                                           currentCalcValue === "");
                        
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        // Calculator is closed, set hook state and open normally
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("amount");
                      }
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.target as HTMLInputElement).blur();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      if (focusedField === "time") {
                        setFocusedField(null);
                        setKeepTimePickerOpen(false);
                      }
                      // Convert formatted amount to raw value and update formData immediately (like web version)
                      const value = formData.amount || "";
                      const rawValue = value && value.trim() !== "" 
                        ? convertFormattedAmountToRawValue(value) 
                        : "0";
                      
                      if (value && value.trim() !== "" && rawValue !== value) {
                        // Update formData immediately so display shows raw value
                        setFormData({ ...formData, amount: rawValue });
                      }
                      
                      // If calculator is already open, only update if user hasn't typed anything new
                      if (focusedField === "amount") {
                        const currentCalcValue = getCalculatorDisplayValue();
                        // Check if user is typing an expression (contains operators)
                        const hasOperators = /[+\-*/]/.test(currentCalcValue);
                        // Check if current value is longer than raw value (user is typing more digits, e.g., "5554" vs "555")
                        const isLonger = currentCalcValue.length > rawValue.length;
                        const startsWithRaw = currentCalcValue.startsWith(rawValue) || rawValue.startsWith(currentCalcValue);
                        
                        // Check if value is already correct - if so, don't update (prevents unnecessary resets)
                        const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                        
                        // Only update if calculator is empty/0 OR if it's different and doesn't have operators/isn't longer
                        // If calculator already matches rawValue/value, don't update (it's already correct)
                        // If calculator has operators or is longer, user is actively typing, so don't overwrite
                        const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (
                                           currentCalcValue === "0" || 
                                           currentCalcValue === "");
                        
                        if (shouldUpdate) {
                          parseExpressionIntoCalculator(rawValue);
                          setCalculatorExpression(rawValue);
                        }
                      } else {
                        // Calculator is closed, set hook state and open normally
                        setCalculatorExpression(rawValue);
                        handleFieldFocus("amount");
                      }
                    }}
                    placeholder="0.00"
                    tabIndex={-1}
                    error={!validation.isValid}
                    helperText={helperText}
                    inputProps={{ inputMode: "none" }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (focusedField === "amount") {
                                setFocusedField(null);
                              }
                              handleFieldFocus("currency");
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (focusedField === "amount") {
                                setFocusedField(null);
                              }
                              handleFieldFocus("currency");
                            }}
                            sx={{
                              minWidth: "auto",
                              px: 1,
                              py: 0.5,
                              color: focusedField === "currency" ? "primary.main" : "text.primary",
                              fontWeight: focusedField === "currency" ? "bold" : "normal",
                              textTransform: "none",
                              touchAction: "manipulation",
                              WebkitTapHighlightColor: "transparent",
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            {currentCurrency}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: focusedField === "amount" ? "primary.main" : "text.secondary",
                        transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                        "&.Mui-focused": {
                          color: focusedField === "amount" ? "primary.main" : "text.secondary",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: focusedField === "amount" ? "primary.main" : "divider",
                          transition: "border-color 0.2s ease",
                        },
                        "&:hover fieldset": {
                          borderColor: focusedField === "amount" ? "primary.main" : "text.primary",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: focusedField === "amount" ? "primary.main" : "divider",
                        },
                      },
                    }}
                  />
                  </Box>
                );
              })()}
            </>
          )}

          {/* Account Selection - Right after Amount */}
          {transactionType === "transfer" ? (
            <>
              <Box data-field="fromAccount" sx={{ width: "100%" }}>
              <TextField
                fullWidth
                label="From Account"
                value={accounts.find(acc => acc.id === transferFormData.fromAccountId)?.accountName || ""}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleFieldFocus("fromAccount");
                }}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLInputElement).blur();
                  handleFieldFocus("fromAccount");
                }}
                onBlur={() => handleFieldBlur("fromAccount")}
                required
                InputProps={{
                  readOnly: true,
                  endAdornment: transferFormData.fromAccountId ? (
                    <InputAdornment position="end">
                      <Button
                        disabled
                        sx={{
                          minWidth: "auto",
                          px: 1,
                          py: 0.5,
                          color: "text.secondary",
                          fontWeight: "normal",
                          textTransform: "none",
                          cursor: "default",
                          "&.Mui-disabled": {
                            color: "text.secondary",
                          },
                        }}
                      >
                        {accounts.find(acc => acc.id === transferFormData.fromAccountId)?.currency || ""}
                      </Button>
                    </InputAdornment>
                  ) : undefined,
                }}
                placeholder="Select account"
                tabIndex={-1}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: focusedField === "fromAccount" ? "primary.main" : "text.secondary",
                    transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                    "&.Mui-focused": {
                      color: focusedField === "fromAccount" ? "primary.main" : "text.secondary",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: focusedField === "fromAccount" ? "primary.main" : "divider",
                      transition: "border-color 0.2s ease",
                    },
                    "&:hover fieldset": {
                      borderColor: focusedField === "fromAccount" ? "primary.main" : "text.primary",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: focusedField === "fromAccount" ? "primary.main" : "divider",
                    },
                  },
                }}
              />
              </Box>
              <Box data-field="toAccount" sx={{ width: "100%" }}>
              <TextField
                fullWidth
                label="To Account"
                value={accounts.find(acc => acc.id === transferFormData.toAccountId)?.accountName || ""}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleFieldFocus("toAccount");
                }}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLInputElement).blur();
                  handleFieldFocus("toAccount");
                }}
                onBlur={() => handleFieldBlur("toAccount")}
                required
                InputProps={{
                  readOnly: true,
                  endAdornment: transferFormData.toAccountId ? (
                    <InputAdornment position="end">
                      <Button
                        disabled
                        sx={{
                          minWidth: "auto",
                          px: 1,
                          py: 0.5,
                          color: "text.secondary",
                          fontWeight: "normal",
                          textTransform: "none",
                          cursor: "default",
                          "&.Mui-disabled": {
                            color: "text.secondary",
                          },
                        }}
                      >
                        {accounts.find(acc => acc.id === transferFormData.toAccountId)?.currency || ""}
                      </Button>
                    </InputAdornment>
                  ) : undefined,
                }}
                placeholder="Select account"
                tabIndex={-1}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: focusedField === "toAccount" ? "primary.main" : "text.secondary",
                    transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                    "&.Mui-focused": {
                      color: focusedField === "toAccount" ? "primary.main" : "text.secondary",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: focusedField === "toAccount" ? "primary.main" : "divider",
                      transition: "border-color 0.2s ease",
                    },
                    "&:hover fieldset": {
                      borderColor: focusedField === "toAccount" ? "primary.main" : "text.primary",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: focusedField === "toAccount" ? "primary.main" : "divider",
                    },
                  },
                }}
              />
              </Box>

              {/* Transfer Exchange Rate (if currencies differ) - Show after To Account */}
              {transferFormData.fromAccountId && transferFormData.toAccountId && (() => {
                const fromAccount = accounts.find(acc => acc.id === transferFormData.fromAccountId);
                const toAccount = accounts.find(acc => acc.id === transferFormData.toAccountId);
                if (!fromAccount || !toAccount) return null;
                
                const currenciesDiffer = fromAccount.currency !== toAccount.currency;
                if (!currenciesDiffer) return null;
                
                const calculatedRate = calculateTransferRate(fromAccount.currency, toAccount.currency);
                const currentRate = transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : null;
                const roundedCalculatedRate = Math.round(calculatedRate * 100000) / 100000;
                const roundedCurrentRate = currentRate !== null && !isNaN(currentRate) ? Math.round(currentRate * 100000) / 100000 : null;
                const rateMatchesSetting = roundedCurrentRate !== null && Math.abs(roundedCurrentRate - roundedCalculatedRate) < 0.000001;
                const shouldDisableRateRefresh = (rateMatchesSetting && !rateManuallyEdited) || !currenciesDiffer;
                
                const transferAmount = transferFormData.amount ? parseFloat(transferFormData.amount) : null;
                const displayRate = focusedField === "rate" ? (getCalculatorDisplayValue() ? parseFloat(getCalculatorDisplayValue()) : null) : currentRate;
                const convertedAmount = transferAmount && displayRate && !isNaN(displayRate) && displayRate > 0
                  ? transferAmount * displayRate
                  : null;
                const fromDecimalPlaces = getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency);
                const toDecimalPlaces = getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency);
                
                const isRateFocused = focusedField === "rate";
                return (
                  <Box data-field="rate" sx={{ width: "100%" }}>
                  <TextField
                    fullWidth
                    required
                    label="Exchange Rate"
                    value={isRateFocused ? getCalculatorDisplayValue() : transferFormData.transferRate || ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                      handleFieldFocus("rate");
                      parseExpressionIntoCalculator(transferFormData.transferRate || "0");
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.target as HTMLInputElement).blur();
                      if (focusedField === "date") {
                        setFocusedField(null);
                      }
                    }}
                    placeholder="Auto-filled from currency settings"
                    helperText={(() => {
                      const rateToUse = displayRate !== null && !isNaN(displayRate) && displayRate > 0
                        ? displayRate
                        : calculatedRate;
                      const rateDisplay = formatRate(rateToUse);
                      const convertedAmountDisplay = convertedAmount 
                        ? formatCurrency(convertedAmount, toAccount.currency, toDecimalPlaces)
                        : "";
                      const rateText = `1 ${fromAccount.currency} = ${rateDisplay} ${toAccount.currency}`;
                      const resultText = convertedAmountDisplay ? ` | = ${convertedAmountDisplay}` : "";
                      return rateText + resultText;
                    })()}
                    tabIndex={-1}
                    inputProps={{ inputMode: "none" }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: isRateFocused ? "primary.main" : "text.secondary",
                        "&.Mui-focused": { color: isRateFocused ? "primary.main" : "text.secondary" },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: isRateFocused ? "primary.main" : "divider" },
                        "&.Mui-focused fieldset": { borderColor: isRateFocused ? "primary.main" : "divider" },
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <span onMouseDown={(e) => e.preventDefault()}>
                            <RefreshIconButton
                              onClick={(e?: React.MouseEvent) => {
                                e?.stopPropagation();
                                e?.preventDefault();
                                handleRefreshRate();
                              }}
                              disabled={shouldDisableRateRefresh}
                              disabledTooltip={rateMatchesSetting ? "Rate matches the setting rate." : "Click to update rate to current currency settings"}
                              tooltip="Refresh rate from currency settings"
                            />
                          </span>
                        </InputAdornment>
                      ),
                    }}
                  />
                  </Box>
                );
              })()}
            </>
          ) : (
            <Box data-field="account" sx={{ width: "100%" }}>
            <TextField
              fullWidth
              label="Account"
              value={accounts.find(acc => acc.id === formData.accountId)?.accountName || ""}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleFieldFocus("account");
              }}
              onFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLInputElement).blur();
                handleFieldFocus("account");
              }}
              onBlur={() => handleFieldBlur("account")}
              required
              InputProps={{
                readOnly: true,
                endAdornment: formData.accountId ? (
                  <InputAdornment position="end">
                    <Button
                      disabled
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.5,
                        color: "text.secondary",
                        fontWeight: "normal",
                        textTransform: "none",
                        cursor: "default",
                        "&.Mui-disabled": {
                          color: "text.secondary",
                        },
                      }}
                    >
                      {accounts.find(acc => acc.id === formData.accountId)?.currency || ""}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
              placeholder="Select account"
              tabIndex={-1}
              sx={{
                "& .MuiInputLabel-root": {
                  color: focusedField === "account" ? "primary.main" : "text.secondary",
                  transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                  "&.Mui-focused": {
                    color: focusedField === "account" ? "primary.main" : "text.secondary",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: focusedField === "account" ? "primary.main" : "divider",
                    transition: "border-color 0.2s ease",
                  },
                  "&:hover fieldset": {
                    borderColor: focusedField === "account" ? "primary.main" : "text.primary",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: focusedField === "account" ? "primary.main" : "divider",
                  },
                },
              }}
            />
            </Box>
          )}

          {/* Exchange Rate (if currencies differ) - Show right after Account, before Category */}
          {transactionType !== "transfer" && formData.accountId && formData.currency && (() => {
            const account = accounts.find(acc => acc.id === formData.accountId);
            const accountCurrency = account?.currency || defaultCurrency;
            const currenciesDiffer = formData.currency !== accountCurrency;
            
            if (!currenciesDiffer) {
              return null;
            }
            
            const calculatedRate = calculateTransferRate(formData.currency, accountCurrency);
            const currentRate = formData.rate ? parseFloat(formData.rate) : null;
            const roundedCalculatedRate = Math.round(calculatedRate * 100000) / 100000;
            const roundedCurrentRate = currentRate !== null && !isNaN(currentRate) ? Math.round(currentRate * 100000) / 100000 : null;
            const rateMatchesSetting = roundedCurrentRate !== null && Math.abs(roundedCurrentRate - roundedCalculatedRate) < 0.000001;
            // Always show refresh button (like web); disable when rate matches setting AND not manually edited (so refresh enables as soon as user edits)
            const shouldDisableRateRefresh = (rateMatchesSetting && !rateManuallyEdited) || formData.currency === accountCurrency;
            
            // Calculate converted amount
            const transactionAmount = formData.amount ? parseFloat(formData.amount) : null;
            const displayRate = focusedField === "rate" ? (getCalculatorDisplayValue() ? parseFloat(getCalculatorDisplayValue()) : null) : currentRate;
            const convertedAmount = transactionAmount && displayRate && !isNaN(displayRate) && displayRate > 0
              ? transactionAmount * displayRate
              : null;
            const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
            const transactionDecimalPlaces = getCurrencyDecimalPlaces(formData.currency, currencies, defaultCurrency);
            
            const rateDisplay = focusedField === "rate" ? (getCalculatorDisplayValue() || "?") : (formData.rate || "?");
            
            const isRateFocused = focusedField === "rate";
            return (
              <Box data-field="rate" sx={{ width: "100%" }}>
              <TextField
                fullWidth
                required
                label="Exchange Rate"
                value={isRateFocused ? getCalculatorDisplayValue() : formData.rate || ""}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (focusedField === "date") {
                    setFocusedField(null);
                  }
                  // Save current calculator to previous field first, then load this field's value (order matters: avoid overwriting rate with default rate or vice versa)
                  handleFieldFocus("rate");
                  parseExpressionIntoCalculator(formData.rate || "0");
                }}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLInputElement).blur();
                  if (focusedField === "date") {
                    setFocusedField(null);
                  }
                  // Do NOT set calculator here: onFocus can run before onClick and would overwrite calculator before handleFieldFocus saves the previous field (e.g. default rate), causing exchange rate to get wrong value
                }}
                placeholder="Auto-filled from currency settings"
                helperText={(() => {
                  // Exchange Rate: "1 ExpenseCurrency = X AccountCurrency | = Result"
                  // The rate stored is from expense currency to account currency
                  const currentRate = formData.rate ? parseFloat(formData.rate) : null;
                  const displayRate = focusedField === "rate" ? (getCalculatorDisplayValue() ? parseFloat(getCalculatorDisplayValue()) : null) : currentRate;
                  const expenseToAccountRate = displayRate !== null && !isNaN(displayRate) && displayRate > 0
                    ? displayRate
                    : (formData.currency !== accountCurrency 
                      ? calculateTransferRate(formData.currency, accountCurrency)
                      : 1);
                  const expenseToAccountRateDisplay = formData.currency !== accountCurrency 
                    ? formatRate(expenseToAccountRate)
                    : "1";
                  
                  // Calculate converted amount (result in account currency)
                  const convertedAmountDisplay = convertedAmount 
                    ? formatCurrency(convertedAmount, accountCurrency, accountDecimalPlaces)
                    : "";
                  
                  // Build helper text: "1 AMD = 0.0027 EUR | = €8.46"
                  const rateText = `1 ${formData.currency} = ${expenseToAccountRateDisplay} ${accountCurrency}`;
                  const resultText = convertedAmountDisplay ? ` | = ${convertedAmountDisplay}` : "";
                  return rateText + resultText;
                })()}
                tabIndex={-1}
                inputProps={{ inputMode: "none" }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: isRateFocused ? "primary.main" : "text.secondary",
                    "&.Mui-focused": { color: isRateFocused ? "primary.main" : "text.secondary" },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: isRateFocused ? "primary.main" : "divider" },
                    "&.Mui-focused fieldset": { borderColor: isRateFocused ? "primary.main" : "divider" },
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <span onMouseDown={(e) => e.preventDefault()}>
                        <RefreshIconButton
                          onClick={(e?: React.MouseEvent) => {
                            e?.stopPropagation();
                            e?.preventDefault();
                            handleRefreshRate();
                          }}
                          disabled={shouldDisableRateRefresh}
                          disabledTooltip={rateMatchesSetting ? "Rate matches the setting rate." : "Click to update rate to current currency settings"}
                          tooltip="Refresh rate from currency settings"
                        />
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
              </Box>
            );
          })()}

          {/* Default Currency Rate (if transaction currency differs from default) - Hide when account is already in default currency (no need for 1 USD = 1 USD) */}
          {transactionType !== "transfer" && formData.currency && formData.currency !== defaultCurrency && (() => {
            const account = accounts.find(acc => acc.id === formData.accountId);
            const accountCurrency = account?.currency || defaultCurrency;
            if (!formData.accountId || accountCurrency === defaultCurrency) return null;
            // Compare account-to-default rate (what the field stores) with calculated account-to-default rate
            const calculatedAccountToDefaultRate = accountCurrency !== defaultCurrency 
              ? calculateTransferRate(accountCurrency, defaultCurrency)
              : 1;
            const currentDefaultRate = formData.defaultCurrencyRate ? parseFloat(formData.defaultCurrencyRate) : null;
            const roundedCalculatedRate = Math.round(calculatedAccountToDefaultRate * 100000) / 100000;
            const roundedCurrentRate = currentDefaultRate !== null && !isNaN(currentDefaultRate) ? Math.round(currentDefaultRate * 100000) / 100000 : null;
            const defaultRateMatchesSetting = roundedCurrentRate !== null && Math.abs(roundedCurrentRate - roundedCalculatedRate) < 0.000001;
            // Disable refresh when rate matches setting (so button is not kept enabled when edit has same rate as settings)
            const shouldDisableDefaultRefresh = defaultRateMatchesSetting || formData.currency === defaultCurrency;
            
            // Calculate amount in default currency for display
            // Field stores account-to-default rate, convert: expenseAmount -> accountAmount -> defaultAmount
            const transactionCurrency = formData.currency || accountCurrency;
            const transactionAmount = formData.amount ? parseFloat(formData.amount) : null;
            const exchangeRate = formData.rate ? parseFloat(formData.rate) : (transactionCurrency === accountCurrency ? 1 : null);
            const displayDefaultRate = focusedField === "defaultCurrencyRate" ? (getCalculatorDisplayValue() ? parseFloat(getCalculatorDisplayValue()) : null) : currentDefaultRate;
            
            // For preview: always use current field value (user input or calculator) when valid, so preview updates as user types
            let accountToDefaultRateForCalc: number;
            if (accountCurrency === defaultCurrency) {
              accountToDefaultRateForCalc = 1;
            } else if (displayDefaultRate !== null && !isNaN(displayDefaultRate) && displayDefaultRate > 0) {
              accountToDefaultRateForCalc = displayDefaultRate;
            } else {
              accountToDefaultRateForCalc = calculateTransferRate(accountCurrency, defaultCurrency);
            }
            
            // First convert expense amount to account currency, then to default currency
            let amountInDefaultCurrency: number | null = null;
            if (transactionAmount) {
              // Handle both cases: same currency (exchangeRate = 1) or different currencies
              const effectiveExchangeRate = exchangeRate !== null && !isNaN(exchangeRate) && exchangeRate > 0 
                ? exchangeRate 
                : (transactionCurrency === accountCurrency ? 1 : null);
              if (effectiveExchangeRate !== null && accountToDefaultRateForCalc > 0) {
                const amountInAccountCurrency = transactionAmount * effectiveExchangeRate;
                amountInDefaultCurrency = amountInAccountCurrency * accountToDefaultRateForCalc;
              }
            }
            const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
            const transactionDecimalPlaces = getCurrencyDecimalPlaces(formData.currency, currencies, defaultCurrency);
            const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
            
            const defaultRateDisplay = focusedField === "defaultCurrencyRate" ? (getCalculatorDisplayValue() || "?") : (formData.defaultCurrencyRate || "?");
            const isDefaultRateFocused = focusedField === "defaultCurrencyRate";

            return (
              <Box data-field="defaultCurrencyRate" sx={{ width: "100%" }}>
              <TextField
                fullWidth
                required
                label="Default Currency Rate"
                value={isDefaultRateFocused ? getCalculatorDisplayValue() : formData.defaultCurrencyRate || ""}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (focusedField === "date") {
                    setFocusedField(null);
                  }
                  // Save current calculator to previous field first (e.g. exchange rate), then load this field's value (order matters: avoid overwriting rate with default rate)
                  handleFieldFocus("defaultCurrencyRate");
                  parseExpressionIntoCalculator(formData.defaultCurrencyRate || "0");
                }}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLInputElement).blur();
                  if (focusedField === "date") {
                    setFocusedField(null);
                  }
                  // Do NOT set calculator here: onFocus can run before onClick and would overwrite calculator before handleFieldFocus saves the previous field (e.g. exchange rate), causing exchange rate to get default rate value
                }}
                placeholder="Auto-filled from currency settings"
                helperText={(() => {
                  // Default Currency Rate: "1 AccountCurrency = DefaultCurrency | = Result"
                  // Use the same accountToDefaultRateForCalc that was used for preview calculation
                  // Show rate with more decimal places for calculation display (keep trailing zeros for clarity)
                  const accountToDefaultRateDisplay = accountCurrency !== defaultCurrency 
                    ? (() => {
                        const rounded = Math.round(accountToDefaultRateForCalc * 100000) / 100000;
                        return rounded.toFixed(5);
                      })()
                    : "1.0000";
                  
                  // Build helper text: "1 EUR = 444.4444 AMD | €0.85 x 444.44... = AMD 376.0"
                  const rateText = `1 ${accountCurrency} = ${accountToDefaultRateDisplay} ${defaultCurrency}`;
                  let calculationText = "";
                  // Use same logic as preview calculation: handle both same and different currencies
                  const effectiveExchangeRate = exchangeRate !== null && !isNaN(exchangeRate) && exchangeRate > 0 ? exchangeRate : (transactionCurrency === accountCurrency ? 1 : null);
                  if (transactionAmount && effectiveExchangeRate !== null && accountToDefaultRateForCalc && amountInDefaultCurrency !== null) {
                    // Show calculation: expenseAmount (converted to account currency) x rate = result
                    const amountInAccountCurrency = transactionAmount * effectiveExchangeRate;
                    const accountAmountFormatted = formatCurrency(amountInAccountCurrency, accountCurrency, accountDecimalPlaces);
                    calculationText = ` | ${accountAmountFormatted} x ${accountToDefaultRateDisplay} = ${formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}`;
                  } else if (amountInDefaultCurrency !== null) {
                    calculationText = ` | = ${formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}`;
                  }
                  return rateText + calculationText;
                })()}
                tabIndex={-1}
                inputProps={{ inputMode: "none" }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: isDefaultRateFocused ? "primary.main" : "text.secondary",
                    "&.Mui-focused": { color: isDefaultRateFocused ? "primary.main" : "text.secondary" },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: isDefaultRateFocused ? "primary.main" : "divider" },
                    "&.Mui-focused fieldset": { borderColor: isDefaultRateFocused ? "primary.main" : "divider" },
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <span onMouseDown={(e) => e.preventDefault()}>
                        <RefreshIconButton
                          onClick={(e?: React.MouseEvent) => {
                            e?.stopPropagation();
                            e?.preventDefault();
                            handleRefreshDefaultCurrencyRate();
                          }}
                          disabled={shouldDisableDefaultRefresh}
                          disabledTooltip={defaultRateMatchesSetting ? "Rate matches the setting rate." : "Click to update rate to current currency settings"}
                          tooltip="Refresh rate from currency settings"
                        />
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
              </Box>
            );
          })()}

          {/* Category (for expense/income only) - After Account and Exchange Rate */}
          {transactionType !== "transfer" && (
            <Box data-field="category" sx={{ width: "100%" }}>
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleFieldFocus("category");
              }}
              onFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLInputElement).blur();
                handleFieldFocus("category");
              }}
              onBlur={() => handleFieldBlur("category")}
              required
              InputProps={{
                readOnly: true,
              }}
              placeholder="Select category"
              tabIndex={-1}
              sx={{
                "& .MuiInputLabel-root": {
                  color: focusedField === "category" ? "primary.main" : "text.secondary",
                  transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  zIndex: 1,
                  "&.Mui-focused": {
                    color: focusedField === "category" ? "primary.main" : "text.secondary",
                  },
                  "&.MuiInputLabel-shrink": {
                    transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: focusedField === "category" ? "primary.main" : "divider",
                    transition: "border-color 0.2s ease",
                  },
                  "&:hover fieldset": {
                    borderColor: focusedField === "category" ? "primary.main" : "text.primary",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: focusedField === "category" ? "primary.main" : "divider",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  "& legend": {
                    "& span": {
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    },
                  },
                },
              }}
            />
            </Box>
          )}

          {/* Category Details - Show only enabled details for selected category with refined tree/branch effect */}
          {transactionType !== "transfer" && formData.category && selectedCategoryDetails.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                ml: 2.5,
                pl: 3,
                position: "relative",
                animation: "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "@keyframes slideDown": {
                  from: {
                    opacity: 0,
                    transform: "translateY(-8px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "0px",
                  top: "-16px",
                  width: "2px",
                  height: "100%",
                  background: "linear-gradient(to bottom, rgba(25, 118, 210, 0.4), rgba(25, 118, 210, 0.6))",
                  borderRadius: "1px",
                  zIndex: 0,
                },
              }}
            >
              <Stack spacing={2.5}>
                {selectedCategoryDetails.map((detail, index) => {
                  const currentValue = formData.details?.[detail.name];
                  
                  if (detail.type === "list" && detail.listItems && detail.listItems.length > 0) {
                    const stringValue = typeof currentValue === "string" ? currentValue : (typeof currentValue === "number" ? String(currentValue) : "");
                    const newlyAddedOptions = newlyAddedDetailOptions[detail.name] || [];
                    
                    return (
                      <Box
                        key={detail.name}
                        sx={{
                          position: "relative",
                          animation: `fadeInBranch 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`,
                          "@keyframes fadeInBranch": {
                            from: {
                              opacity: 0,
                              transform: "translateX(-8px)",
                            },
                            to: {
                              opacity: 1,
                              transform: "translateX(0)",
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, position: "relative" }}>
                          <Box
                            sx={{
                              position: "absolute",
                              left: "-20px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: "18px",
                              height: "2px",
                              background: "linear-gradient(to right, rgba(25, 118, 210, 0.6), rgba(25, 118, 210, 0.4))",
                              borderRadius: "0 1px 1px 0",
                              zIndex: 1,
                            }}
                          />
                          <Box sx={{ position: "relative", flex: 1 }}>
                            <FormControl fullWidth sx={{ position: "relative" }}>
                              <InputLabel 
                                id={`detail-${detail.name}-label`} 
                                shrink={true}
                                sx={{
                                  backgroundColor: "background.paper",
                                  paddingLeft: "4px",
                                  paddingRight: "4px",
                                  zIndex: 1,
                                }}
                              >
                                {detail.name}
                              </InputLabel>
                              <Select
                                labelId={`detail-${detail.name}-label`}
                                value={stringValue || ""}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    details: {
                                      ...(formData.details || {}),
                                      [detail.name]: e.target.value,
                                    },
                                  });
                                  setOpenDetailLists({
                                    ...openDetailLists,
                                    [detail.name]: false,
                                  });
                                }}
                                onOpen={() => {
                                  setOpenDetailLists({
                                    ...openDetailLists,
                                    [detail.name]: true,
                                  });
                                  if (focusedField) {
                                    setFocusedField(null);
                                  }
                                }}
                                onClose={() => {
                                  setOpenDetailLists({
                                    ...openDetailLists,
                                    [detail.name]: false,
                                  });
                                }}
                                onClick={(e) => {
                                  if (focusedField) {
                                    setFocusedField(null);
                                  }
                                }}
                                onFocus={() => {
                                  if (focusedField) {
                                    setFocusedField(null);
                                  }
                                }}
                                label={detail.name}
                                displayEmpty
                                renderValue={(value) => {
                                  if (!value || value === "") {
                                    return <Box component="em" sx={{ color: "text.secondary" }}>Select...</Box>;
                                  }
                                  return value;
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      zIndex: 1400,
                                      maxHeight: "300px",
                                    },
                                  },
                                  MenuListProps: {
                                    sx: {
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                    },
                                  },
                                }}
                                sx={{
                                  position: "relative",
                                  zIndex: 1,
                                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                  "&:hover": {
                                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                                  },
                                  "&.Mui-focused": {
                                    backgroundColor: "rgba(25, 118, 210, 0.06)",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    "& legend": {
                                      "& span": {
                                        paddingLeft: "4px",
                                        paddingRight: "4px",
                                      },
                                    },
                                  },
                                  "& .MuiSelect-select": {
                                    paddingRight: stringValue && newlyAddedOptions.includes(stringValue) && !openDetailLists[detail.name] ? "60px !important" : "40px !important",
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select...</em>
                                </MenuItem>
                                {detail.listItems.map((item) => (
                                  <MenuItem key={item} value={item}>
                                    {item}
                                  </MenuItem>
                                ))}
                                {newlyAddedOptions.map((item) => (
                                  <MenuItem 
                                    key={item} 
                                    value={item}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{item}</Box>
                                    {openDetailLists[detail.name] && (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          const updatedOptions = (newlyAddedDetailOptions[detail.name] || []).filter(opt => opt !== item);
                                          setNewlyAddedDetailOptions({
                                            ...newlyAddedDetailOptions,
                                            [detail.name]: updatedOptions,
                                          });
                                          if (formData.details?.[detail.name] === item) {
                                            setFormData({
                                              ...formData,
                                              details: {
                                                ...(formData.details || {}),
                                                [detail.name]: "",
                                              },
                                            });
                                          }
                                        }}
                                        sx={{
                                          padding: "4px",
                                          color: "error.main",
                                          flexShrink: 0,
                                          "&:hover": {
                                            backgroundColor: "rgba(211, 47, 47, 0.08)",
                                          },
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {/* Delete button - positioned on right side when newly added option is selected */}
                            {stringValue && newlyAddedOptions.includes(stringValue) && !openDetailLists[detail.name] && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const updatedOptions = (newlyAddedDetailOptions[detail.name] || []).filter(opt => opt !== stringValue);
                                  setNewlyAddedDetailOptions({
                                    ...newlyAddedDetailOptions,
                                    [detail.name]: updatedOptions,
                                  });
                                  setFormData({
                                    ...formData,
                                    details: {
                                      ...(formData.details || {}),
                                      [detail.name]: "",
                                    },
                                  });
                                }}
                                sx={{
                                  position: "absolute",
                                  right: "40px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  padding: "4px",
                                  color: "error.main",
                                  zIndex: 2,
                                  "&:hover": {
                                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          {/* Add button - positioned outside the FormControl container, on the right side */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFocusedField(null); // Close keyboard/calculator/panel so add-option dialog is usable
                              setAddOptionDialogDetailName(detail.name);
                              setAddOptionDialogValue("");
                              setAddOptionDialogError("");
                              setAddOptionDialogOpen(true);
                            }}
                            sx={{
                              flexShrink: 0,
                              padding: "4px",
                              color: "primary.main",
                              alignSelf: "center",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                              },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {(stringValue && newlyAddedOptions.includes(stringValue)) || newlyAddedOptions.length > 0 ? (
                          <Box sx={{ width: "100%", mt: 0.5, ml: 0 }}>
                            {stringValue && newlyAddedOptions.includes(stringValue) ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "warning.main",
                                  fontSize: "0.75rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                Will be saved to "{detail.name}" list in settings
                              </Typography>
                            ) : newlyAddedOptions.length > 0 ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "warning.main",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {newlyAddedOptions.length} new detail{newlyAddedOptions.length > 1 ? "s" : ""} added
                              </Typography>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  } else if (detail.type === "measurable") {
                    const numValue = typeof currentValue === "number" ? currentValue : (typeof currentValue === "string" && currentValue !== "" ? parseFloat(currentValue) : "");
                    const isThisDetailFocused = focusedField === `detail:${detail.name}`;
                    const displayValue = isThisDetailFocused ? getCalculatorDisplayValue() : (numValue === "" || numValue === undefined || numValue === null ? "" : numValue);
                    return (
                      <Box
                        key={detail.name}
                        sx={{
                          position: "relative",
                          animation: `fadeInBranch 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`,
                          "@keyframes fadeInBranch": {
                            from: {
                              opacity: 0,
                              transform: "translateX(-8px)",
                            },
                            to: {
                              opacity: 1,
                              transform: "translateX(0)",
                            },
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: "-20px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "18px",
                            height: "2px",
                            background: "linear-gradient(to right, rgba(25, 118, 210, 0.6), rgba(25, 118, 210, 0.4))",
                            borderRadius: "0 1px 1px 0",
                            zIndex: 1,
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <TextField
                            fullWidth
                            inputProps={{ "data-field": `detail:${detail.name}`, readOnly: true, inputMode: "none" }}
                            label={detail.name}
                            type="text"
                            value={displayValue === "" || displayValue === undefined || displayValue === null ? "" : displayValue}
                            onFocus={(e) => {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                              handleFieldFocus(`detail:${detail.name}` as FocusedField);
                            }}
                            onClick={() => {
                              handleFieldFocus(`detail:${detail.name}` as FocusedField);
                            }}
                            InputLabelProps={{
                              shrink: true,
                              sx: {
                                backgroundColor: "background.paper",
                                paddingLeft: "4px",
                                paddingRight: "4px",
                                zIndex: 1,
                              },
                            }}
                            InputProps={{
                              endAdornment: detail.unit ? (
                                <InputAdornment position="end">{detail.unit}</InputAdornment>
                              ) : undefined,
                              sx: {
                                position: "relative",
                                zIndex: 1,
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                                },
                                "&.Mui-focused": {
                                  backgroundColor: "rgba(25, 118, 210, 0.06)",
                                },
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-notchedOutline": {
                                "& legend": {
                                  "& span": {
                                    paddingLeft: "4px",
                                    paddingRight: "4px",
                                  },
                                },
                              },
                            }}
                            placeholder={detail.unit ? `Enter ${detail.unit}` : "Enter value"}
                          />
                          <IconButton
                            size="small"
                            disabled
                            sx={{
                              mt: 1,
                              color: "text.disabled",
                              "&.Mui-disabled": {
                                color: "text.disabled",
                              },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })}
              </Stack>
            </Box>
          )}

          {/* Expense fee (when workspace allows) - same UI logic as transfer fee */}
          {transactionType === "expense" && allowExpenseFee && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  type="button"
                  size="small"
                  onClick={() => {
                    if (formData.feeAmount === undefined) {
                      setFormData({ ...formData, feeAmount: "", feeAccountId: "" });
                      setCalculatorExpression("0");
                      setTimeout(() => handleFieldFocus("feeAmount"), 0);
                    } else {
                      setFormData({ ...formData, feeAmount: undefined, feeAccountId: undefined });
                    }
                  }}
                  disabled={!!(editingTransaction && editingTransaction.type === "expense" && formData.feeAmount !== undefined && formData.feeAmount !== "")}
                  sx={{ minWidth: "auto", px: 1, py: 0.5, textTransform: "none", "&.Mui-disabled": { color: "text.disabled" } }}
                >
                  {formData.feeAmount === undefined ? "+ Add fee" : "Remove fee"}
                </Button>
              </Box>
              {formData.feeAmount !== undefined && (
                <Stack direction="row" spacing={2}>
                  <Box data-field="feeAmount" sx={{ flex: 1 }}>
                    <TextField
                      required
                      label="Fee Amount"
                      value={focusedField === "feeAmount" ? getCalculatorDisplayValue() : (formData.feeAmount || "")}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (focusedField === "date") setFocusedField(null);
                        const value = formData.feeAmount || "";
                        const rawValue = value && value.trim() !== "" ? convertFormattedAmountToRawValue(value) : "0";
                        if (value && value.trim() !== "" && rawValue !== value) {
                          setFormData({ ...formData, feeAmount: rawValue });
                        }
                        if (focusedField === "feeAmount") {
                          const currentCalcValue = getCalculatorDisplayValue();
                          const hasOperators = /[+\-*/]/.test(currentCalcValue);
                          const isLonger = currentCalcValue.length > rawValue.length;
                          const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                          const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (currentCalcValue === "0" || currentCalcValue === "");
                          if (shouldUpdate) {
                            parseExpressionIntoCalculator(rawValue);
                            setCalculatorExpression(rawValue);
                          }
                        } else {
                          setCalculatorExpression(rawValue);
                          handleFieldFocus("feeAmount");
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (focusedField === "date") setFocusedField(null);
                        const value = formData.feeAmount || "";
                        const rawValue = value && value.trim() !== "" ? convertFormattedAmountToRawValue(value) : "0";
                        if (value && value.trim() !== "" && rawValue !== value) {
                          setFormData({ ...formData, feeAmount: rawValue });
                        }
                        if (focusedField === "feeAmount") {
                          const currentCalcValue = getCalculatorDisplayValue();
                          const hasOperators = /[+\-*/]/.test(currentCalcValue);
                          const isLonger = currentCalcValue.length > rawValue.length;
                          const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                          const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (currentCalcValue === "0" || currentCalcValue === "");
                          if (shouldUpdate) {
                            parseExpressionIntoCalculator(rawValue);
                            setCalculatorExpression(rawValue);
                          }
                        } else {
                          setCalculatorExpression(rawValue);
                          handleFieldFocus("feeAmount");
                        }
                      }}
                      onFocus={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        (e.target as HTMLInputElement).blur();
                        if (focusedField === "date") setFocusedField(null);
                        const value = formData.feeAmount || "";
                        const rawValue = value && value.trim() !== "" ? convertFormattedAmountToRawValue(value) : "0";
                        if (value && value.trim() !== "" && rawValue !== value) {
                          setFormData({ ...formData, feeAmount: rawValue });
                        }
                        if (focusedField === "feeAmount") {
                          const currentCalcValue = getCalculatorDisplayValue();
                          const hasOperators = /[+\-*/]/.test(currentCalcValue);
                          const isLonger = currentCalcValue.length > rawValue.length;
                          const alreadyMatches = currentCalcValue === rawValue || currentCalcValue === value;
                          const shouldUpdate = !hasOperators && !isLonger && !alreadyMatches && (currentCalcValue === "0" || currentCalcValue === "");
                          if (shouldUpdate) {
                            parseExpressionIntoCalculator(rawValue);
                            setCalculatorExpression(rawValue);
                          }
                        } else {
                          setCalculatorExpression(rawValue);
                          handleFieldFocus("feeAmount");
                        }
                      }}
                      onBlur={() => handleFieldBlur("feeAmount")}
                      inputProps={{ inputMode: "none", tabIndex: -1 }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: formData.feeAccountId ? (
                          <InputAdornment position="end">
                            <Typography variant="body2" color="text.secondary">
                              {accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived)?.currency || ""}
                            </Typography>
                          </InputAdornment>
                        ) : undefined,
                      }}
                      sx={{
                        width: "100%",
                        "& .MuiInputLabel-root": {
                          color: focusedField === "feeAmount" ? "primary.main" : "text.secondary",
                          transition: "color 0.2s ease",
                          "&.Mui-focused": { color: focusedField === "feeAmount" ? "primary.main" : "text.secondary" },
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: focusedField === "feeAmount" ? "primary.main" : "divider",
                            borderWidth: focusedField === "feeAmount" ? "2px" : "1px",
                            transition: "border-color 0.2s ease, border-width 0.2s ease",
                          },
                          "&:hover fieldset": { borderColor: focusedField === "feeAmount" ? "primary.main" : "text.primary" },
                          "&.Mui-focused fieldset": {
                            borderColor: focusedField === "feeAmount" ? "primary.main" : "divider",
                            borderWidth: focusedField === "feeAmount" ? "2px" : "1px",
                          },
                        },
                      }}
                    />
                  </Box>
                  <TextField
                    label="Fee Account"
                    value={accounts.find((acc) => acc.id === formData.feeAccountId)?.accountName || ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleFieldFocus("feeAccount");
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.target as HTMLInputElement).blur();
                      handleFieldFocus("feeAccount");
                    }}
                    onBlur={() => handleFieldBlur("feeAccount")}
                    required
                    InputProps={{
                      readOnly: true,
                      endAdornment: formData.feeAccountId ? (
                        <InputAdornment position="end">
                          <Button
                            disabled
                            sx={{
                              minWidth: "auto",
                              px: 1,
                              py: 0.5,
                              color: "text.secondary",
                              fontWeight: "normal",
                              textTransform: "none",
                              cursor: "default",
                              "&.Mui-disabled": { color: "text.secondary" },
                            }}
                          >
                            {accounts.find((acc) => acc.id === formData.feeAccountId)?.currency || ""}
                          </Button>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    placeholder="Select account"
                    tabIndex={-1}
                    sx={{
                      flex: 1,
                      "& .MuiInputLabel-root": {
                        color: focusedField === "feeAccount" ? "primary.main" : "text.secondary",
                        transition: "color 0.2s ease, transform 0.2s cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                        "&.Mui-focused": { color: focusedField === "feeAccount" ? "primary.main" : "text.secondary" },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: focusedField === "feeAccount" ? "primary.main" : "divider",
                          borderWidth: focusedField === "feeAccount" ? "2px" : "1px",
                          transition: "border-color 0.2s ease, border-width 0.2s ease",
                        },
                        "&:hover fieldset": { borderColor: focusedField === "feeAccount" ? "primary.main" : "text.primary" },
                        "&.Mui-focused fieldset": {
                          borderColor: focusedField === "feeAccount" ? "primary.main" : "divider",
                          borderWidth: focusedField === "feeAccount" ? "2px" : "1px",
                        },
                      },
                    }}
                  />
                </Stack>
              )}
            </>
          )}

          {/* Custom fields (workspace-configured; note/attachment are custom fields) */}
          {customFields.length > 0 && (
            <>
              <input
                ref={isTransfer ? transferCustomFieldFileInputRef : customFieldFileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const targetId = customFieldFileTargetRef.current;
                  const file = e.target.files?.[0];
                  if (targetId && file) {
                    if (isTransfer) {
                      setTransferFormData((prev) => ({
                        ...prev,
                        customFieldPendingFiles: {
                          ...(prev.customFieldPendingFiles || {}),
                          [targetId]: file,
                        },
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        customFieldPendingFiles: {
                          ...(prev.customFieldPendingFiles || {}),
                          [targetId]: file,
                        },
                      }));
                    }
                  }
                  e.target.value = "";
                }}
              />
              <Stack spacing={1.5} sx={{ width: "100%" }}>
                {customFields.map((field) => {
                  const value = isTransfer ? transferFormData.customFieldValues?.[field.id] : formData.customFieldValues?.[field.id];
                  const pendingFile = isTransfer ? transferFormData.customFieldPendingFiles?.[field.id] : formData.customFieldPendingFiles?.[field.id];
                  const attachmentValue = value && typeof value === "object" && "url" in value ? (value as CustomFieldAttachmentValue) : null;
                  const displayName = pendingFile?.name ?? attachmentValue?.name ?? "";
                  const fileInputRefForType = isTransfer ? transferCustomFieldFileInputRef : customFieldFileInputRef;

                  if (field.type === "text") {
                    return (
                      <TextField
                        key={field.id}
                        fullWidth
                        label={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <TransactionFieldIcon icon={field.icon} />
                            <span>{field.label}</span>
                          </Stack>
                        }
                        value={typeof value === "string" ? value : ""}
                        onChange={(e) => {
                          if (isTransfer) {
                            setTransferFormData((prev) => ({
                              ...prev,
                              customFieldValues: {
                                ...(prev.customFieldValues || {}),
                                [field.id]: e.target.value,
                              },
                            }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              customFieldValues: {
                                ...(prev.customFieldValues || {}),
                                [field.id]: e.target.value,
                              },
                            }));
                          }
                        }}
                        onClick={() => {
                          if (focusedField) setFocusedField(null);
                          setIsOnOptionalField(true);
                        }}
                        onFocus={() => setIsOnOptionalField(true)}
                        onBlur={() => setTimeout(() => setIsOnOptionalField(false), 100)}
                      />
                    );
                  }
                  return (
                    <Box key={field.id}>
                      <TextField
                        fullWidth
                        label={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <TransactionFieldIcon icon={field.icon} />
                            <span>{field.label}</span>
                          </Stack>
                        }
                        value={displayName}
                        placeholder={`${field.label} (PDF, PNG, JPG…) · Max 10MB`}
                        onClick={(e) => {
                          if (focusedField) setFocusedField(null);
                          setIsOnOptionalField(true);
                          customFieldFileTargetRef.current = field.id;
                          fileInputRefForType.current?.click();
                        }}
                        onFocus={() => setIsOnOptionalField(true)}
                        onBlur={() => setTimeout(() => setIsOnOptionalField(false), 100)}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {displayName ? (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isTransfer) {
                                        setTransferFormData((prev) => {
                                          const next = { ...prev };
                                          if (next.customFieldPendingFiles?.[field.id]) {
                                            const { [field.id]: _, ...rest } = next.customFieldPendingFiles;
                                            next.customFieldPendingFiles = Object.keys(rest).length ? rest : undefined;
                                          }
                                          if (next.customFieldValues?.[field.id]) {
                                            const cv = { ...(next.customFieldValues || {}) };
                                            delete cv[field.id];
                                            next.customFieldValues = Object.keys(cv).length ? cv : undefined;
                                          }
                                          return next;
                                        });
                                      } else {
                                        setFormData((prev) => {
                                          const next = { ...prev };
                                          if (next.customFieldPendingFiles?.[field.id]) {
                                            const { [field.id]: _, ...rest } = next.customFieldPendingFiles;
                                            next.customFieldPendingFiles = Object.keys(rest).length ? rest : undefined;
                                          }
                                          if (next.customFieldValues?.[field.id]) {
                                            const cv = { ...(next.customFieldValues || {}) };
                                            delete cv[field.id];
                                            next.customFieldValues = Object.keys(cv).length ? cv : undefined;
                                          }
                                          return next;
                                        });
                                      }
                                    }}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      customFieldFileTargetRef.current = field.id;
                                      fileInputRefForType.current?.click();
                                    }}
                                    color="primary"
                                  >
                                    <AttachFileIcon fontSize="small" />
                                  </IconButton>
                                </>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    customFieldFileTargetRef.current = field.id;
                                    fileInputRefForType.current?.click();
                                  }}
                                  color="primary"
                                >
                                  <AttachFileIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ),
                        }}
                        helperText={undefined}
                        sx={{ cursor: "pointer" }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
  );
};

export default MobileTransactionFormFields;
